import type { SupabaseClient } from '@supabase/supabase-js';
import { InfrastructureError } from '@dma/shared';
import { ContactMessage, type ContactMessageStatus } from '../domain/entities/ContactMessage.entity';
import { EmailAddress } from '../domain/value-objects/EmailAddress.vo';
import { PhoneNumber } from '../domain/value-objects/PhoneNumber.vo';
import type { ContactMessageRepository } from '../domain/repositories/ContactMessageRepository.interface';

const TABLE_NAME = 'contact_messages';

interface ContactMessageRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string;
  message: string;
  status: ContactMessageStatus;
  created_at: string;
}

function rowToEntity(row: ContactMessageRow): ContactMessage {
  return ContactMessage.reconstitute({
    id: row.id,
    name: row.name,
    email: EmailAddress.create(row.email),
    phone: row.phone ? PhoneNumber.create(row.phone) : null,
    subject: row.subject,
    message: row.message,
    status: row.status,
    createdAt: row.created_at
  });
}

function entityToRow(entity: ContactMessage): ContactMessageRow {
  return {
    id: entity.id,
    name: entity.name,
    email: entity.email.value,
    phone: entity.phone?.value ?? null,
    subject: entity.subject,
    message: entity.message,
    status: entity.status,
    created_at: entity.createdAt
  };
}

/**
 * Infrastructure implementation of ContactMessageRepository, backed by the
 * `contact_messages` Supabase table (see
 * infra/supabase/migrations/001_create_contact_messages.sql and
 * infra/supabase/policies/001_contact_messages_policies.sql).
 * Per docs/architecture/05-error-handling-strategy.md: raw Supabase errors
 * are caught here and never leak past this layer as-is.
 */
export class SupabaseContactMessageRepository implements ContactMessageRepository {
  constructor(private readonly client: SupabaseClient) {}

  public async save(entity: ContactMessage): Promise<void> {
    const { error } = await this.client.from(TABLE_NAME).insert(entityToRow(entity));
    if (error) {
      throw new InfrastructureError('CONTACT_MESSAGE_INSERT_FAILED', error.message, { cause: error });
    }
  }

  public async findById(id: string): Promise<ContactMessage | null> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('id', id)
      .maybeSingle<ContactMessageRow>();

    if (error) {
      throw new InfrastructureError('CONTACT_MESSAGE_FETCH_FAILED', error.message, { cause: error });
    }
    return data ? rowToEntity(data) : null;
  }

  public async findAll(): Promise<ContactMessage[]> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .order('created_at', { ascending: false })
      .returns<ContactMessageRow[]>();

    if (error) {
      throw new InfrastructureError('CONTACT_MESSAGE_LIST_FAILED', error.message, { cause: error });
    }
    return (data ?? []).map(rowToEntity);
  }

  public async delete(id: string): Promise<void> {
    const { error } = await this.client.from(TABLE_NAME).delete().eq('id', id);
    if (error) {
      throw new InfrastructureError('CONTACT_MESSAGE_DELETE_FAILED', error.message, { cause: error });
    }
  }
}
